//Andy Hu - 6/28-2021
//Implement Zoom in Effect for Reviwe & Product Gallery

const px = (v: number): string => v + "px"
const loupeClassName = "__loupe";

export type LoupeOptions = {
	magnification?: number // magnification level (default: 2.25)
	width?: number | string // width of the loupe (default: 250)
	height?: number | string // height of the loupe (default: 250/1.6)
	container?: Node // containing node (default: document.body)
	additionalClassName?: string // additional class name to add to the loupe element (default: undefined)
	style?: Partial<CSSStyleDeclaration> // additional styles to add to the loupe element (default: undefined)
	shape?: Shape // shape of the loupe: "rectangle" | "circle" (default: "rectangle")
}

// Shape represents the shape of the loupe.
export type Shape = "rectangle" | "circle"

// A Loupe represents the loupe element and its properties such as
// magnification level and size.
//
// Use enableLoupe to add the loupe to an image-displaying element of your
// choice.
export class Loupe {
	readonly magnification: number
	readonly width: string
	readonly height: string
	readonly elem: HTMLDivElement
	readonly shape: Shape
	readonly container: Node

	constructor({
		magnification = 1.5,
		width = 144,
		height = 144,
		container = document.body,
		additionalClassName,
		style,
		shape = "rectangle"
	}: LoupeOptions = {}) {
		this.magnification = magnification
		this.width = typeof width === "number" ? px(width) : width
		this.height = typeof height === "number" ? px(height) : height
		this.container = container
		this.shape = shape
		if(document.getElementsByClassName(loupeClassName).length > 0) //loupe already exist
		{
			this.elem = document.getElementsByClassName(loupeClassName)[0] as HTMLDivElement;
		}
		else
		{
			this.elem = document.createElement("div");
			this.elem.classList.add(loupeClassName);
			if (additionalClassName !== undefined) {
				this.elem.classList.add(additionalClassName)
			}
			if (style !== undefined) {
				Object.assign(this.elem.style, style)
			}
			this.container.appendChild(this.elem)
		}

	}

	unmount(): void {
		this.container.removeChild(this.elem)
	}
}

// enableLoupe adds the specified loupe object to the target element. The target
// element can be, for instance, an <img> element or a <div> element with a
// background-image.
//
// enableLoupe returns a function that can be later be used to disable the loupe
// on the target element.
export const enableLoupe = (target: HTMLElement | SVGElement, imgUrl: string, loupe: Loupe, bottomOffset: number=0 ) => {
	const doc = target.ownerDocument
	const wnd = doc.defaultView

	const resetTouchScroll = disableTouchScroll(target)

	let timerID = 0; //timer to adjust size of loupe - for bug fixing

	const moveHandlers: {
		docMouseMoveHandler: ((e: MouseEvent) => void) | undefined
		docTouchMoveHandler: ((e: TouchEvent) => void) | undefined
	} = {
		docMouseMoveHandler: undefined,
		docTouchMoveHandler: undefined,
	}

	const stopMouseMove = () => {
		Object.assign(loupe.elem.style, { display: "none", visibility: "hidden" })
		doc.removeEventListener("mousemove", moveHandlers.docMouseMoveHandler)
		timerID?? clearTimeout(timerID);
	}
	const stopTouchMove = () => {
		if (moveHandlers.docTouchMoveHandler !== undefined) {
			Object.assign(loupe.elem.style, { display: "none", visibility: "hidden" })
			doc.removeEventListener("touchmove", moveHandlers.docTouchMoveHandler)
			moveHandlers.docTouchMoveHandler = undefined
		}
		timerID?? clearTimeout(timerID);
	}

	// reset the initial value
	let width = 0;
	let height = 0;
	loupe.elem.removeAttribute('data-url');
	const loadZoomingImage = () => {
		let bigImgUrl = (window as any).loupeImg;
		if (!loupe.elem.getAttribute('data-url') || (loupe.elem.getAttribute('data-url') !== bigImgUrl)) {
			const zoomingImg = document.createElement('img');
			  loupe.elem.setAttribute('data-url', bigImgUrl);
        zoomingImg.src = bigImgUrl;
				zoomingImg.onload = function() {
					Object.assign(loupe.elem.style, {
						backgroundSize: zoomingImg.width * loupe.magnification + 'px ' + zoomingImg.height * loupe.magnification + "px",
						backgroundImage: 'url("' + bigImgUrl + '")'
					});
					// update width height
					width = zoomingImg.width;
					height = zoomingImg.height;
				}
		}
	}

	const handler = function() {
		Object.assign(target.style, { cursor: "none" })
		Object.assign(loupe.elem.style, { display: "block", visibility: "hidden" })

		const exitDistance = 0
		
		let targetRect = target.getBoundingClientRect();

		if(targetRect.left == 0)
		{
			console.log('error');
		}

		// left and top copied from jQuery $(offset)
		// https://j11y.io/jquery/#v=1.11.2&fn=jQuery.fn.offset
		let left = targetRect.left + (wnd?.pageXOffset || doc.documentElement.scrollLeft) - (doc.documentElement.clientLeft || 0)
		let top = targetRect.top + (wnd?.pageYOffset || doc.documentElement.scrollTop) - (doc.documentElement.clientTop || 0)
		let right = left + targetRect.width;
		let bottom = top + targetRect.height;

		if (!loupe.elem.getAttribute('data-url')) {
			Object.assign(loupe.elem.style, {
				backgroundSize: width * loupe.magnification + 'px ' + height * loupe.magnification + "px",
				backgroundImage: 'url(data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==)',
				width: loupe.width,
				height: loupe.height
			});
		}

		loadZoomingImage();

		if (loupe.shape === "circle") {
			Object.assign(loupe.elem.style, { borderRadius: "50%" })
		}

		const loupeOffset = loupe.elem.getBoundingClientRect().width / 2;

		moveHandlers.docMouseMoveHandler = function(e: MouseEvent) {
			if (
				$('.js-main-image').hasClass("model") ||
				$('.js-main-image').hasClass("video") ||
				(window as any).isVideoSlide ||
				$(target).hasClass("video") ||
				(window as any).isModelSlide ||
				$(target).hasClass("model") ||
				e.pageX < left - exitDistance ||
				e.pageX > right + exitDistance ||
				e.pageY < top - exitDistance ||
				e.pageY > bottom + exitDistance - bottomOffset) {
				stopMouseMove()
				return
			}

			Object.assign(loupe.elem.style, { display: "block", visibility: "visible" })
			
			const positionX = -e.pageX + left;
			const percentX = positionX * 100 / targetRect.width;
			const zoomingPercentX = width * loupe.magnification * percentX / 100 + loupeOffset;

			const positionY = -e.pageY + top;
			const percentY = positionY * 100 / targetRect.height;
			const zoomingPercentY = height * loupe.magnification * percentY / 100 + loupeOffset;

			const bgPosX = zoomingPercentX;
			const bgPosY = zoomingPercentY;
			Object.assign(loupe.elem.style, {
				left: px(e.pageX - loupeOffset),
				top: px(e.pageY - loupeOffset),
				backgroundPosition: px(bgPosX) + " " + px(bgPosY)
			});
		}

		moveHandlers.docTouchMoveHandler = function(e: TouchEvent) {
			const t = e.touches.item(0)
			if (!t) {
				return
			}

			if (
				$('.js-main-image').hasClass("model") ||
				$(target).hasClass("video") ||
				$(target).hasClass("model") ||
				t.pageX < left - exitDistance  ||
				t.pageX > right + exitDistance ||
				t.pageY < top - exitDistance ||
				t.pageY > bottom + exitDistance) {
				stopTouchMove()
				return
			}
			Object.assign(loupe.elem.style, { display: "block", visibility: "visible" })

			const positionX = -t.pageX + left
			const percentX = positionX * 100 / targetRect.width
			const zoomingPercentX = width * loupe.magnification * percentX / 100 + loupeOffset

			const positionY = -t.pageY + top
			const percentY = positionY * 100 / targetRect.height
			const zoomingPercentY = height * loupe.magnification * percentY / 100 + loupeOffset

			const bgPosX = zoomingPercentX
			const bgPosY = zoomingPercentY
			Object.assign(loupe.elem.style, {
				left: px(t.pageX - loupeOffset),
				top: px(t.pageY - loupeOffset),
				backgroundPosition: px(bgPosX) + " " + px(bgPosY)
			});
		}

		timerID?? clearTimeout(timerID);
		timerID = window.setTimeout(()=>{

			loadZoomingImage();
			
			const sectionHeight = $(".images-container .images-section").height();
			const imgHeight = $("#js-current-image img").height();
			const bottomMargin = (sectionHeight - imgHeight) > 155? 0 : (155 - (sectionHeight - imgHeight));
			bottomOffset = bottomMargin;
			
			target = (window as any).loupeTarget = $('#js-current-image')[0];
			targetRect = target.getBoundingClientRect();
			left = targetRect.left + (wnd?.pageXOffset || doc.documentElement.scrollLeft) - (doc.documentElement.clientLeft || 0)
			top = targetRect.top + (wnd?.pageYOffset || doc.documentElement.scrollTop) - (doc.documentElement.clientTop || 0)
			right = left + targetRect.width;
			bottom = top + targetRect.height;

		}, 500);	//for bug fix

		doc.addEventListener("mousemove", moveHandlers.docMouseMoveHandler)
		doc.addEventListener("touchmove", moveHandlers.docTouchMoveHandler)
	}

	if($(target).hasClass("video") || $(target).hasClass("model") )
	{
		return;
	}

	target.addEventListener("mouseover", handler)
	target.addEventListener("touchstart", handler)

	return () => {
		doc.removeEventListener('mousemove', moveHandlers.docMouseMoveHandler);
		doc.removeEventListener('touchmove', moveHandlers.docTouchMoveHandler);
		
		target.removeEventListener("mouseover", handler)
		target.removeEventListener("touchstart", handler);
		resetTouchScroll()
		stopMouseMove()
		stopTouchMove()
	}
}

const disableTouchScroll = (elem: HTMLElement | SVGElement): (() => void) => {
	const old = elem.style.touchAction
	Object.assign(elem.style, { touchAction: "none" }) // https://stackoverflow.com/a/43275544/3309046
	return () => { Object.assign(elem.style, { touchAction: old }) }
}